import { httpsCallable } from 'firebase/functions';
import { functions } from './firebase';
import { getMetaBrowserIds } from './metaPixel';
import { isOneTimePlan, type PlanId, type BillingInterval } from '@shared/types';

// Lets us kick off the payment-intent creation the instant the user clicks the
// "Get started" CTA — in parallel with the 1.3s thanos snap — so by the time
// CheckoutPage mounts the clientSecret is already in hand. Hides the
// cloud-function + Stripe API roundtrip behind the animation rather than
// after it.
//
// Two backing objects, one shape: subscription plans get an incomplete Stripe
// Subscription (-> subscriptionId), lifetime gets a bare PaymentIntent
// (-> paymentIntentId). Both hand back a clientSecret the Payment Element can
// confirm, so everything downstream of here is identical.

interface PrefetchResult {
  clientSecret: string;
  customerId: string;
  subscriptionId?: string;
  paymentIntentId?: string;
  // 'setup' when the plan carries a free trial: a trialing subscription has no
  // invoice, so Stripe backs the clientSecret with a SetupIntent, which the client
  // must confirm via confirmSetup() rather than confirmPayment().
  mode?: 'payment' | 'setup';
  planId: PlanId;
  billingInterval?: BillingInterval;
  ts: number;
}

const STORAGE_KEY = 'checkout_prefetch';
// clientSecrets are tied to short-lived PaymentIntents; reusing one minutes
// later risks a stale-intent error from Stripe. 60s comfortably covers the
// snap + navigation while keeping the window tight.
const TTL_MS = 60_000;

let inflight: Promise<PrefetchResult> | null = null;
let inflightKey: string | null = null;

export function prefetchCheckoutSubscription(
  planId: PlanId,
  billingInterval: BillingInterval = 'month',
): Promise<PrefetchResult> {
  // Key the in-flight cache on BOTH plan and interval. Keying on plan alone would
  // hand a monthly clientSecret to someone who toggled to annual, and they would be
  // charged the wrong amount.
  const key = `${planId}:${billingInterval}`;
  if (inflight && inflightKey === key) return inflight;

  inflightKey = key;
  const referral = (window as unknown as { Rewardful?: { referral?: string } }).Rewardful?.referral;
  const create = httpsCallable<
    { planId: PlanId; billingInterval?: BillingInterval; referral?: string; metaFbp?: string; metaFbc?: string },
    {
      clientSecret: string;
      customerId: string;
      subscriptionId?: string;
      paymentIntentId?: string;
      mode?: 'payment' | 'setup';
      billingInterval?: BillingInterval;
    }
  >(functions, isOneTimePlan(planId) ? 'createGuestLifetimePayment' : 'createGuestSubscription');

  // Lifetime takes no interval — its callable's schema rejects unknown keys.
  const payload = isOneTimePlan(planId)
    ? { planId, ...(referral ? { referral } : {}) }
    : { planId, billingInterval, ...(referral ? { referral } : {}), ...getMetaBrowserIds() };

  inflight = create(payload)
    .then(({ data }) => {
      const result: PrefetchResult = { ...data, planId, billingInterval, ts: Date.now() };
      try {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(result));
      } catch {
        // sessionStorage disabled or full — module-level promise is still
        // available for same-tab consumption, so this is non-fatal.
      }
      return result;
    })
    .catch((err) => {
      inflight = null;
      inflightKey = null;
      throw err;
    });

  return inflight;
}

// The interval is part of the identity of a prefetch, not a detail of it.
//
// Matching on planId alone would let a monthly clientSecret — minted when the user
// first hovered the CTA — be handed to a checkout page rendering the ANNUAL offer.
// The page would say $2,490/year and Stripe would charge $249/month. The user gets
// the wrong product at the wrong price, and finds out on their statement.
export function consumeCheckoutPrefetch(
  planId: PlanId,
  billingInterval: BillingInterval = 'month',
): PrefetchResult | Promise<PrefetchResult> | null {
  const key = `${planId}:${billingInterval}`;

  // 1. In-flight from the same SPA session — return the live promise so the
  //    consumer awaits the same call instead of firing a duplicate.
  if (inflight && inflightKey === key) {
    const p = inflight;
    inflight = null;
    inflightKey = null;
    return p;
  }
  // 2. Resolved before navigation — pick up from sessionStorage.
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    sessionStorage.removeItem(STORAGE_KEY);
    const result = JSON.parse(raw) as PrefetchResult;
    if (result.planId !== planId) return null;
    if ((result.billingInterval ?? 'month') !== billingInterval) return null;
    if (Date.now() - result.ts > TTL_MS) return null;
    return result;
  } catch {
    return null;
  }
}
